import { useState, useCallback, useEffect } from 'react';
import { Payment, SortConfig, PAYMENTS_PAGE_SIZE, PAYMENTS_PAGE_SIZE_OPTIONS } from './types';
import {
    PaymentsTable,
    PaymentCard,
    EmptyState,
} from './components';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Label } from '@/components/ui';
import { useQueryParams } from '@/hooks';
import { cn } from '@/lib/utils';
import { useRequestGetUserPayments } from '@/hooks/httpRequest';
import { Loader2, SearchX } from 'lucide-react';
import PaymentsToolbar from './components/PaymentsToolbar';
import { CoinPackageNameEnum, PaymentPlatformEnum } from '@/enums';

interface ProfileDetailUserDataPaymentsProps {
    userId: string;
}

/**
 * Payments tab content component.
 * Displays payment history with responsive design:
 * - Desktop: Table view with sortable columns
 * - Mobile: Card-based layout
 * Features pagination (10 per page) and stagger animations.
 */
const ProfileDetailUserDataPayments = ({ userId }: ProfileDetailUserDataPaymentsProps) => {
    // URL-based pagination for proper browser history
    const {
        values: [pageParam],
        setValue,
        updateSearchParams,
        urlSearchParams,
    } = useQueryParams(['paymentPage'], { paymentPage: '1' });
    const currentPage = parseInt(pageParam as string, 10) || 1;
    const pageSizeParam = urlSearchParams.get('paymentPageSize');
    const currentPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : PAYMENTS_PAGE_SIZE;

    // Initialize pageSize in URL if not present
    useEffect(() => {
        if (!pageSizeParam) {
            updateSearchParams(prev => {
                prev.set('paymentPageSize', String(PAYMENTS_PAGE_SIZE));
                return prev;
            });
        }
    }, [pageSizeParam, updateSearchParams]);

    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: 'createdAt',
        direction: 'desc',
    });

    // Current filters from URL (applied filters)
    const provider = urlSearchParams.get('provider') as PaymentPlatformEnum | null;
    const packageName = urlSearchParams.get('packageName') as CoinPackageNameEnum | null;
    const startDate = urlSearchParams.get('startDate');
    const endDate = urlSearchParams.get('endDate');
    const minPriceParam = urlSearchParams.get('minPrice');
    const maxPriceParam = urlSearchParams.get('maxPrice');
    const minPrice = minPriceParam ? parseFloat(minPriceParam) : undefined;
    const maxPrice = maxPriceParam ? parseFloat(maxPriceParam) : undefined;

    // Check if any filters are active
    const hasActiveFilters = !!(provider || packageName || startDate || endDate || minPrice !== undefined || maxPrice !== undefined);

    // Fetch payments from API with filters & server-side sorting
    const { payments, pagination, isLoadingPayments, error } = useRequestGetUserPayments({
        userId,
        page: currentPage,
        pageSize: currentPageSize,
        provider: provider ?? undefined,
        packageName: packageName ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        minPrice: minPrice !== undefined && !isNaN(minPrice) ? minPrice : undefined,
        maxPrice: maxPrice !== undefined && !isNaN(maxPrice) ? maxPrice : undefined,
        sortBy: sortConfig.key as 'createdAt' | 'priceInEur' | 'packageName' | 'provider',
        sortOrder: sortConfig.direction,
    });

    console.log(isLoadingPayments ? 'Loading...' : payments);

    // Transform API data to match component's Payment type
    const paginatedPayments: Payment[] = payments.map(p => ({
        _id: p._id,
        userId: userId,
        currency: p.currency,
        price: p.price,
        priceInEur: p.priceInEur,
        provider: p.provider,
        transactionId: p.transactionId,
        packageName: p.packageName,
        createdAt: p.createdAt,
    }));

    const totalPages = pagination?.pageCount ?? 1;
    const totalItems = pagination?.itemCount ?? 0;

    // Handle sort change (triggers server-side sorting)
    const handleSort = useCallback((key: 'createdAt' | 'priceInEur' | 'packageName' | 'provider', order: 'asc' | 'desc') => {
        setSortConfig({
            key,
            direction: order,
        });
        // Reset to page 1 when sorting changes
        setValue('paymentPage', '1');
    }, [setValue]);

    // Handle page change
    const handlePageChange = useCallback((page: number) => {
        setValue('paymentPage', String(page));
    }, [setValue]);

    // Handle page size change
    const handlePageSizeChange = useCallback((newPageSize: string) => {
        updateSearchParams(prev => {
            prev.set('paymentPageSize', newPageSize);
            prev.set('paymentPage', '1'); // Reset to first page when page size changes
            return prev;
        });
    }, [updateSearchParams]);

    // Loading state
    if (isLoadingPayments) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="flex items-center justify-center py-12 text-destructive">
                Erreur lors du chargement des paiements
            </div>
        );
    }

    // Empty state: only show when there are NO filters and NO payments (truly empty)
    // If filters are active but no results, we'll show empty state in the table instead
    const showEmptyState = payments.length === 0 && currentPage === 1 && !hasActiveFilters;
    const showEmptyFilteredState = payments.length === 0 && hasActiveFilters;

    // Generate pagination items
    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages
            for (let i = 1; i <= totalPages; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink
                            isActive={currentPage === i}
                            onClick={() => handlePageChange(i)}
                            className="cursor-pointer"
                        >
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                );
            }
        } else {
            // Show first, last, and pages around current
            items.push(
                <PaginationItem key={1}>
                    <PaginationLink
                        isActive={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                        className="cursor-pointer"
                    >
                        1
                    </PaginationLink>
                </PaginationItem>
            );

            if (currentPage > 3) {
                items.push(
                    <PaginationItem key="ellipsis-start">
                        <PaginationEllipsis />
                    </PaginationItem>
                );
            }

            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink
                            isActive={currentPage === i}
                            onClick={() => handlePageChange(i)}
                            className="cursor-pointer"
                        >
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                );
            }

            if (currentPage < totalPages - 2) {
                items.push(
                    <PaginationItem key="ellipsis-end">
                        <PaginationEllipsis />
                    </PaginationItem>
                );
            }

            items.push(
                <PaginationItem key={totalPages}>
                    <PaginationLink
                        isActive={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        className="cursor-pointer"
                    >
                        {totalPages}
                    </PaginationLink>
                </PaginationItem>
            );
        }

        return items;
    };

    // Show empty state (no filters, no payments) - replaces everything
    if (showEmptyState) {
        return (
            <div className="space-y-6 animate-in fade-in duration-300">
                <PaymentsToolbar disabled={isLoadingPayments} />
                <EmptyState />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            <PaymentsToolbar disabled={isLoadingPayments} />

            {/* Desktop Table View */}
            <div className="hidden md:block">
                <PaymentsTable
                    payments={paginatedPayments}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    showEmptyFilteredState={showEmptyFilteredState}
                />
            </div>

            {/* Mobile Card View */}
            {showEmptyFilteredState ? (
                <div className="md:hidden">
                    <div className="rounded-md border p-8 text-center">
                        <div className="flex flex-col items-center justify-center gap-2">
                            <div className="rounded-full bg-muted p-3">
                                <SearchX className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-foreground">
                                    Aucun paiement ne correspond à vos filtres
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Essayez de modifier vos critères de recherche pour voir plus de résultats.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="md:hidden space-y-4">
                    {paginatedPayments.map((payment, index) => (
                        <PaymentCard key={payment._id} payment={payment} index={index} />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {(totalPages > 1 || totalItems > 0) && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
                    <div className="flex items-center gap-4 whitespace-nowrap">
                        <p className="text-sm text-muted-foreground">
                            Page {currentPage} sur {totalPages} ({totalItems} résultats)
                        </p>
                        <div className="flex items-center gap-2">
                            <Label htmlFor="pageSize" className="text-xs text-muted-foreground whitespace-nowrap">
                                Par page:
                            </Label>
                            <Select
                                value={String(currentPageSize)}
                                onValueChange={handlePageSizeChange}
                                disabled={isLoadingPayments}
                            >
                                <SelectTrigger id="pageSize" className="h-8 w-16 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PAYMENTS_PAGE_SIZE_OPTIONS.map((size) => (
                                        <SelectItem key={size} value={String(size)}>
                                            {size}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {totalPages > 1 && (
                        <Pagination>
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                        className={cn(
                                            'cursor-pointer',
                                            currentPage === 1 && 'pointer-events-none opacity-50'
                                        )}
                                    />
                                </PaginationItem>
                                {renderPaginationItems()}
                                <PaginationItem>
                                    <PaginationNext
                                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                        className={cn(
                                            'cursor-pointer',
                                            currentPage === totalPages && 'pointer-events-none opacity-50'
                                        )}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProfileDetailUserDataPayments;
